<template>
  <div class="rate-quote-carrier">
    <div class="flex flex-gap-1 w-full">
      <CarrierAvatar :carrier="carrier" size="large" />

      <div v-if="carrier" class="carrier-details">
        <span
          v-if="carrier.name"
          class="rate-quote-carrier-name font-medium text-overflow-ellipsis overflow-x-hidden"
        >
          {{ carrier.name }}
        </span>

        <span class="rate-quote-carrier-scac">
          {{ carrier.scac }}
        </span>

        <span v-if="hasBrokeredCarrier">
          {{
            t('brokerages.providedByParam', {
              param: rateQuote.carrier?.name,
            })
          }}
        </span>
      </div>
    </div>

    <div
      v-if="isCarrierMessagingEnabled && rateQuote.carrier && withContextChat"
      class="rate-quote-carrier-context-chat flex align-items-center"
    >
      <CarrierMessagingContextChatButton
        v-if="showContextChat"
        :options="carrierContactItemOptions"
        :loading="loadingCarrierContacts"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useFlag } from '@unleash/proxy-client-vue';
import type { PropType } from 'vue';
import { computed, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useMessagingContacts } from '@rygen/rygen-ui/composables/carrierMessaging';

import { Carrier, RateQuote } from '@/model';

import CarrierAvatar from '@rygen/rygen-ui/components/carrier/CarrierAvatar.vue';
import CarrierMessagingContextChatButton from '@rygen/rygen-ui/components/carrier/messaging/CarrierMessagingContextChatButton.vue';

const props = defineProps({
  rateQuote: {
    type: Object as PropType<RateQuote>,
    default: () => new RateQuote(),
  },
  withContextChat: {
    type: Boolean,
    default: () => false,
  },
});
const { rateQuote, withContextChat } = toRefs(props);

const isCarrierMessagingEnabled = useFlag('carrier-messaging-feat-flag');

const { t } = useI18n();
const {
  carrierContactItemOptions,
  isVisible: showContextChat,
  loadCarrierContactsByCarrier,
  loading: loadingCarrierContacts,
} = useMessagingContacts();

const hasBrokeredCarrier = computed(
  () =>
    !!(
      rateQuote.value.brokeredCarrier?.scac ||
      rateQuote.value.brokeredCarrier?.name
    ),
);

const carrier = computed(() => {
  const brokeredCarrier = rateQuote.value.brokeredCarrier;
  return hasBrokeredCarrier.value && brokeredCarrier
    ? Carrier.fromCarrierRef(brokeredCarrier)
    : rateQuote.value.carrier;
});

/**
 * Watch carrier ID rather than the full rateQuote or rateQuote.carrier object,
 * since the RateQuote is rebuilt on every RateQuote fetch poll response, which would otherwise
 * trigger loadCarrierContactsByCarrier() unnecessarily on each poll cycle.
 */
watch(
  () => carrier.value?.id,
  (newVal) => {
    if (
      isCarrierMessagingEnabled.value &&
      withContextChat.value &&
      newVal &&
      carrier.value
    ) {
      loadCarrierContactsByCarrier(carrier.value.toCarrierRef());
    }
  },
  { immediate: true },
);
</script>

<style>
.rate-quote-carrier {
  display: flex;
  justify-content: space-between;

  .carrier-details {
    display: flex;
    flex-direction: column;
    width: calc(100% - var(--p-avatar-lg-width) - var(--gap-1));
  }
}
</style>
